import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { GlassCard } from '../components/GlassCard';
import { useUserStore } from '../store/userStore';
import { submitAssessment } from '../lib/api';

function Slider({ label, value, onChange, color = '#7C3AED' }) {
  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-1.5">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-sm font-semibold" style={{ color }}>{value}</span>
      </div>
      <input
        type="range"
        min={0}
        max={10}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        className="w-full accent-[var(--aura-purple)]"
        style={{ accentColor: color }}
      />
    </div>
  );
}

function Segmented({ label, options, value, onChange }) {
  return (
    <div className="mb-4">
      <div className="text-sm font-medium mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            className="rounded-full px-3.5 py-2 text-sm border transition-base"
            style={value === o.value ? { background: 'var(--aura-purple)', borderColor: 'var(--aura-purple)', color: '#fff' } : { borderColor: 'var(--aura-line)' }}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Toggle({ label, value, onChange }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <span className="text-sm font-medium">{label}</span>
      <button onClick={() => onChange(!value)} className="relative w-12 h-7 rounded-full transition-base" style={{ background: value ? 'var(--aura-purple)' : 'var(--aura-line)' }}>
        <span className="absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-base" style={{ left: value ? 22 : 2 }} />
      </button>
    </div>
  );
}

export default function HealthAssessment() {
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    hot_flashes: 0, night_sweats: 0, irregular_periods: false, mood_fluctuations: 0,
    weight_change: 'none', energy_crashes: 0, fatigue_level: 0, cravings: 0, hunger_spikes: 0,
    activity_motivation: 5, family_diabetes: false, family_cardiovascular: false,
    difficulty_falling_asleep: 0, sleep_fragmentation: 0, early_waking: 0, non_restorative_sleep: 0, sleep_overheating: 0, wired_but_tired: 0,
    anxiety_level: 0, overthinking: 0, low_mood: 0, brain_fog: 0, resilience_level: 5,
    caffeine_timing: 'morning', sleep_routine_consistency: 5, physical_activity_level: 5, stress_level: 5, alcohol_frequency: 'none',
  });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      await submitAssessment({ ...f, user_id: user.id });
      toast.success('Assessment complete');
      navigate('/assessment/results');
    } catch (e) {
      toast.error('Could not process assessment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--aura-bg)' }}>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6 pb-16">
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => navigate('/app')} className="grid place-items-center w-9 h-9 rounded-full bg-white border border-[var(--aura-line)]"><i className="fa-solid fa-arrow-left" /></button>
          <h1 className="font-display text-2xl sm:text-3xl">Precision Health Assessment</h1>
        </div>
        <p className="text-[var(--aura-ink-soft)] mb-6 ml-12">Rate each area from 0 (not at all) to 10 (severe). We’ll map how they connect.</p>

        <GlassCard className="p-6 mb-4">
          <h3 className="font-display text-lg mb-4 flex items-center gap-2"><i className="fa-solid fa-droplet" style={{ color: '#8B5CF6' }} /> Hormonal</h3>
          <Slider label="Hot flushes" value={f.hot_flashes} onChange={set('hot_flashes')} color="#8B5CF6" />
          <Slider label="Night sweats" value={f.night_sweats} onChange={set('night_sweats')} color="#8B5CF6" />
          <Slider label="Mood fluctuations" value={f.mood_fluctuations} onChange={set('mood_fluctuations')} color="#8B5CF6" />
          <Toggle label="Irregular periods" value={f.irregular_periods} onChange={set('irregular_periods')} />
        </GlassCard>

        <GlassCard className="p-6 mb-4">
          <h3 className="font-display text-lg mb-4 flex items-center gap-2"><i className="fa-solid fa-fire" style={{ color: '#F97316' }} /> Metabolic</h3>
          <Segmented label="Weight change" value={f.weight_change} onChange={set('weight_change')} options={[
            { value: 'none', label: 'No change' }, { value: 'gain_central', label: 'Central gain' }, { value: 'gain_general', label: 'General gain' }, { value: 'loss', label: 'Loss' },
          ]} />
          <Slider label="Energy crashes" value={f.energy_crashes} onChange={set('energy_crashes')} color="#F97316" />
          <Slider label="Fatigue" value={f.fatigue_level} onChange={set('fatigue_level')} color="#F97316" />
          <Slider label="Cravings" value={f.cravings} onChange={set('cravings')} color="#F97316" />
          <Slider label="Hunger spikes" value={f.hunger_spikes} onChange={set('hunger_spikes')} color="#F97316" />
          <Slider label="Activity motivation" value={f.activity_motivation} onChange={set('activity_motivation')} color="#F97316" />
          <Toggle label="Family history: diabetes" value={f.family_diabetes} onChange={set('family_diabetes')} />
          <Toggle label="Family history: cardiovascular" value={f.family_cardiovascular} onChange={set('family_cardiovascular')} />
        </GlassCard>

        <GlassCard className="p-6 mb-4">
          <h3 className="font-display text-lg mb-4 flex items-center gap-2"><i className="fa-solid fa-moon" style={{ color: '#6366F1' }} /> Sleep &amp; recovery</h3>
          <Slider label="Difficulty falling asleep" value={f.difficulty_falling_asleep} onChange={set('difficulty_falling_asleep')} color="#6366F1" />
          <Slider label="Waking during night" value={f.sleep_fragmentation} onChange={set('sleep_fragmentation')} color="#6366F1" />
          <Slider label="Early waking (2-4am)" value={f.early_waking} onChange={set('early_waking')} color="#6366F1" />
          <Slider label="Waking unrefreshed" value={f.non_restorative_sleep} onChange={set('non_restorative_sleep')} color="#6366F1" />
          <Slider label="Overheating at night" value={f.sleep_overheating} onChange={set('sleep_overheating')} color="#6366F1" />
          <Slider label="Wired but tired" value={f.wired_but_tired} onChange={set('wired_but_tired')} color="#6366F1" />
        </GlassCard>

        <GlassCard className="p-6 mb-4">
          <h3 className="font-display text-lg mb-4 flex items-center gap-2"><i className="fa-solid fa-brain" style={{ color: '#14B8A6' }} /> Mental wellbeing</h3>
          <Slider label="Anxiety" value={f.anxiety_level} onChange={set('anxiety_level')} color="#14B8A6" />
          <Slider label="Overthinking" value={f.overthinking} onChange={set('overthinking')} color="#14B8A6" />
          <Slider label="Low mood" value={f.low_mood} onChange={set('low_mood')} color="#14B8A6" />
          <Slider label="Brain fog" value={f.brain_fog} onChange={set('brain_fog')} color="#14B8A6" />
          <Slider label="Resilience" value={f.resilience_level} onChange={set('resilience_level')} color="#14B8A6" />
        </GlassCard>

        <GlassCard className="p-6 mb-6">
          <h3 className="font-display text-lg mb-4 flex items-center gap-2"><i className="fa-solid fa-leaf" style={{ color: '#EC4899' }} /> Lifestyle</h3>
          <Segmented label="Caffeine timing" value={f.caffeine_timing} onChange={set('caffeine_timing')} options={[
            { value: 'morning', label: 'Morning' }, { value: 'afternoon', label: 'Afternoon' }, { value: 'evening', label: 'Evening' }, { value: 'none', label: 'None' },
          ]} />
          <Slider label="Sleep routine consistency" value={f.sleep_routine_consistency} onChange={set('sleep_routine_consistency')} color="#EC4899" />
          <Slider label="Physical activity" value={f.physical_activity_level} onChange={set('physical_activity_level')} color="#EC4899" />
          <Slider label="Stress level" value={f.stress_level} onChange={set('stress_level')} color="#EC4899" />
          <Segmented label="Alcohol frequency" value={f.alcohol_frequency} onChange={set('alcohol_frequency')} options={[
            { value: 'none', label: 'None' }, { value: 'occasional', label: 'Occasional' }, { value: 'moderate', label: 'Moderate' }, { value: 'frequent', label: 'Frequent' },
          ]} />
        </GlassCard>

        <button data-testid="submit-assessment-btn" onClick={submit} disabled={saving} className="w-full aura-btn-gradient text-white font-semibold rounded-2xl py-4 flex items-center justify-center gap-2 disabled:opacity-70">
          {saving ? <i className="fa-solid fa-circle-notch fa-spin" /> : <><i className="fa-solid fa-wand-magic-sparkles" /> Reveal my patterns</>}
        </button>
      </div>
    </div>
  );
}
